/**
 * @fileoverview Logo - Brand logo SVG component
 *
 * Purpose: Displays application logo with customizable size and styling
 * Dependencies: None (inline SVG)
 * Used by: Navigation, headers, authentication pages
 */

import { cn } from "@/utils/misc";

interface LogoProps {
  /** Logo width in pixels */
  width?: number;
  /** Logo height in pixels */
  height?: number;
  /** Additional CSS classes for styling */
  className?: string;
  /** Compact version (shows only icon bars, no text) */
  compact?: boolean;
  /** Additional SVG props */
  [key: string]: unknown | undefined;
}

/**
 * Logo component displaying the application brand mark
 *
 * Features:
 * - Customizable width and height
 * - Inherits text color from parent (uses currentColor)
 * - Accepts additional SVG props
 * - Responsive and accessible
 * - Compact mode (icon bars only, no wordmark)
 *
 * @component
 * @example
 * // Default full logo
 * <Logo />
 *
 * // Compact version (icon only)
 * <Logo compact />
 *
 * // Custom size
 * <Logo width={200} height={51} />
 *
 * // Custom color via className
 * <Logo className="text-green-600" />
 */
export function Logo({
  width,
  height,
  className,
  compact = false,
  ...args
}: LogoProps) {
  if (compact) {
    // Compact version: Just the icon bars (left side of logo)
    return (
      <svg
        {...args}
        width={width ?? 40}
        height={height ?? 41}
        xmlns="http://www.w3.org/2000/svg"
        className={cn(className)}
        viewBox="0 0 101 125"
        fill="none"
      >
        <rect y="29" width="11" height="69" rx="1" fill="currentColor" />
        <rect
          x="101"
          y="98"
          width="11"
          height="69"
          rx="1"
          transform="rotate(180 101 98)"
          fill="currentColor"
        />
        <rect x="15" y="37" width="11" height="54" rx="1" fill="currentColor" />
        <rect
          x="86"
          y="91"
          width="11"
          height="54"
          rx="1"
          transform="rotate(180 86 91)"
          fill="currentColor"
        />
        <rect x="30" y="47" width="11" height="34" rx="1" fill="currentColor" />
        <rect
          x="71"
          y="81"
          width="11"
          height="34"
          rx="1"
          transform="rotate(180 71 81)"
          fill="currentColor"
        />
        <rect x="45" y="56" width="11" height="16" rx="1" fill="currentColor" />
      </svg>
    );
  }

  // Full version: icon bars (SVG) + "Morgan" wordmark rendered as live text.
  //
  // The wordmark used to be a baked SVG vector outline, which has no adjustable
  // font weight. Rendering it as live text in Lora medium lets us control the
  // weight and keeps it consistent with the sidebar/footer wordmark. Sizing is
  // driven by `height` (the icon scales to fill it and the text is sized
  // proportionally), so existing callers that pass `height` keep working.
  const resolvedHeight = height ?? 41;

  // The outer wrapper carries the caller's className (color + responsive
  // show/hide utilities like `hidden md:block`). The inner element owns the
  // flex layout so a caller-supplied `display` utility can't break the
  // icon + wordmark row.
  return (
    <span {...args} className={cn(className)}>
      <span
        className="inline-flex items-center"
        style={{ height: resolvedHeight, gap: resolvedHeight * 0.26 }}
      >
        <svg
          height="100%"
          xmlns="http://www.w3.org/2000/svg"
          className="h-full w-auto"
          viewBox="0 0 101 125"
          fill="none"
        >
          <rect y="29" width="11" height="69" rx="1" fill="currentColor" />
          <rect
            x="101"
            y="98"
            width="11"
            height="69"
            rx="1"
            transform="rotate(180 101 98)"
            fill="currentColor"
          />
          <rect
            x="15"
            y="37"
            width="11"
            height="54"
            rx="1"
            fill="currentColor"
          />
          <rect
            x="86"
            y="91"
            width="11"
            height="54"
            rx="1"
            transform="rotate(180 86 91)"
            fill="currentColor"
          />
          <rect
            x="30"
            y="47"
            width="11"
            height="34"
            rx="1"
            fill="currentColor"
          />
          <rect
            x="71"
            y="81"
            width="11"
            height="34"
            rx="1"
            transform="rotate(180 71 81)"
            fill="currentColor"
          />
          <rect
            x="45"
            y="56"
            width="11"
            height="16"
            rx="1"
            fill="currentColor"
          />
        </svg>
        <span
          className="font-medium leading-none tracking-tight"
          style={{
            fontFamily: "'Lora', serif",
            fontSize: resolvedHeight * 0.82,
          }}
        >
          Morgan
        </span>
      </span>
    </span>
  );
}
